import { useMailRuntime } from "../newsletter/context/MailRuntime";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Subscribers from "./Subscribers";
import { newsletterPreview } from "../newsletter/email/newsletterPreview";
import { mailIdentity, mailDashboardRequest, normalizeMailItems, metric } from "../newsletter/mailDashboardApi";
const safeDate = value => { const date = value ? new Date(value) : null; return date && Number.isFinite(date.getTime()) ? date : null; };
const formatShortDateTime = value => safeDate(value)?.toLocaleString() || '';
const sorted = items => items.sort((a,b) => (safeDate(b.displayDate)?.getTime() || 0) - (safeDate(a.displayDate)?.getTime() || 0));
const Mailboxes = [
  { id: 'compose newsletter', name: 'Compose Newsletter' },
  { id: 'newsletters', name: 'Sent' }, { id: 'newsletter drafts', name: 'Drafts' },
  { id: 'inbox', name: 'Inbox' }, { id: 'sent', name: 'Sent Mail' }, { id: 'deleted', name: 'Trash' },
];
export default function MailCenter() {
  const runtime = useMailRuntime();
  const previewPanel = useRef(null);
  const { API_LIST_NEWSLETTERS, API_GET_NEWSLETTER, API_NEWSLETTER_STATS, API_SUBSCRIBER_ENGAGEMENT } = runtime.api;
  const [refresh, setRefresh] = useState(0), [error, setError] = useState(''), [checking, setChecking] = useState(true);
  const [mailAddonEnabled, setMailAddonEnabled] = useState(false), [attrs, setAttrs] = useState(null);
  const [activeView, setActiveView] = useState('mail'), [subscriberView, setSubscriberView] = useState('manage');
  const [selectedMailbox, setSelectedMailbox] = useState('newsletters'), [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null), [preview, setPreview] = useState(null);
  const [loadingList, setLoadingList] = useState(true), [loadingPreview, setLoadingPreview] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false), [newsletterStats, setNewsletterStats] = useState(null);
  const [selectedEngagement, setSelectedEngagement] = useState(null), [statsLoading, setStatsLoading] = useState(true);
  const [listError, setListError] = useState(''), [previewError, setPreviewError] = useState('');
  const [statsError, setStatsError] = useState(''), [engagementError, setEngagementError] = useState('');
  const [nextToken, setNextToken] = useState(null), [pageToken, setPageToken] = useState('');
  const [previewAttempt, setPreviewAttempt] = useState(0);
  const [listAttempt, setListAttempt] = useState(0);
  const isSent = selectedMailbox === 'newsletters', isDraft = selectedMailbox === 'newsletter drafts';
  const mailboxTitle = useMemo(() => Mailboxes.find(m => m.id === selectedMailbox)?.name || 'Mailbox', [selectedMailbox]);
  const available = !checking && mailAddonEnabled && Boolean(attrs?.sub);
  const leave = () => window.dispatchEvent(new Event('bookish:before-navigation', { cancelable: true }));
  const go = url => { if (leave()) window.location.assign(url); };
  const openDraftInComposer = item => go(runtime.composerUrl + (runtime.composerUrl.includes('?') ? '&' : '?') + 'newsletterId=' + encodeURIComponent(item.id) + '&mode=draft');
  const selectMailbox = id => { if (!leave()) return; setSidebarOpen(false); setPageToken(''); setActiveView('mail'); setSelectedMailbox(id); };
  const selectSubscribers = view => { if (!leave()) return; setSidebarOpen(false); setActiveView('subscribers'); setSubscriberView(view); };
  const refreshMail = () => { if (!leave()) return; setPageToken(''); setRefresh(value => value + 1); };

  useEffect(() => {
    let active = true; setChecking(true); setError(''); setAttrs(null); setMailAddonEnabled(false); setLoadingList(true); setStatsLoading(true); setNewsletterStats(null);
    mailIdentity(runtime).then(({ attributes }) => { if (active) { setAttrs(attributes); setMailAddonEnabled(attributes['custom:onboardingcomplete'] === 'true'); } })
      .catch(() => { if (active) setError('Unable to verify your Mail Center account. Please retry or return to your portal.'); })
      .finally(() => { if (active) setChecking(false); });
    return () => { active = false; };
  }, [runtime, refresh]);

  useEffect(() => {
    if (!available) return;
    let active = true; setStatsLoading(true); setStatsError(''); setNewsletterStats(null);
    mailDashboardRequest(runtime, attrs.sub, API_NEWSLETTER_STATS + '?authorSub=' + encodeURIComponent(attrs.sub))
      .then(data => { if (active) {
        const value = data.aggregate ?? data;
        if (!value || typeof value !== 'object' || !['totalRecipients','totalSubscribers','avgOpenRate','avgClickRate','avgCTOR'].some(key => Object.hasOwn(value,key))) throw Error('Statistics are unavailable.');
        setNewsletterStats(Object.fromEntries(['totalRecipients','totalSubscribers','avgOpenRate','avgClickRate','avgCTOR'].map(key => [key,metric(value[key])])));
      } }).catch(() => { if (active) setStatsError('Newsletter statistics could not be loaded. Use Refresh mail to retry.'); })
      .finally(() => { if (active) setStatsLoading(false); });
    return () => { active = false; };
  }, [available, attrs, runtime, API_NEWSLETTER_STATS]);

  useEffect(() => {
    if (!available || activeView !== 'mail' || (!isSent && !isDraft)) return;
    let active = true; setLoadingList(true); setListError('');
    if (!pageToken) { setItems([]); setSelectedItem(null); setPreview(null); setNextToken(null); }
    const type = isDraft ? 'drafts' : 'sent';
    mailDashboardRequest(runtime, attrs.sub, API_LIST_NEWSLETTERS + '?type=' + type + (pageToken ? '&nextToken=' + encodeURIComponent(pageToken) : ''))
      .then(data => {
        const normalized = sorted(normalizeMailItems(data,type)); if (!active) return;
        if (data.nextToken && data.nextToken === pageToken) throw Error('The next page could not be loaded. Please refresh mail.');
        setItems(previous => sorted([...new Map([...(pageToken ? previous : []),...normalized].map(item=>[item.id,item])).values()]));
        setNextToken(data.nextToken || null); if (!pageToken && normalized.length) setSelectedItem(normalized[0]);
      }).catch(e => { if (active) setListError(e.message || 'Unable to load newsletters. Please refresh mail.'); })
      .finally(() => { if (active) setLoadingList(false); });
    return () => { active = false; };
  }, [available, attrs, runtime, activeView, isSent, isDraft, pageToken, listAttempt, API_LIST_NEWSLETTERS]);

  useEffect(() => {
    setPreview(null); setPreviewError(''); setLoadingPreview(false);
    if (!available || activeView !== 'mail' || !selectedItem) return;
    let active = true; setLoadingPreview(true);
    mailDashboardRequest(runtime, attrs.sub, API_GET_NEWSLETTER + '?id=' + encodeURIComponent(selectedItem.id) + '&type=' + selectedItem.type)
      .then(data => {
        const n = data.newsletter || data;
        if (!n || typeof n !== 'object' || !['html','body'].some(key => typeof n[key] === 'string')) throw Error('Newsletter content could not be read.');
        if (active) setPreview({ subject: typeof n.subject === 'string' ? n.subject : selectedItem.subject, html: newsletterPreview(n, selectedItem.type), delivery: { accepted:metric(n.accepted ?? n.succeeded),failed:metric(n.failed),unknown:metric(n.unknown),pending:metric(n.pending),skipped:metric(n.skipped),cancelled:metric(n.cancelled) } });
      }).catch(e => { if (active) setPreviewError(e.message || 'Unable to load the preview.'); })
      .finally(() => { if (active) setLoadingPreview(false); });
    return () => { active = false; };
  }, [available, attrs, runtime, activeView, selectedItem, API_GET_NEWSLETTER, previewAttempt]);

  useEffect(() => {
    setSelectedEngagement(null); setEngagementError('');
    if (!available || activeView !== 'mail' || !selectedItem || !isSent) return;
    let active = true;
    mailDashboardRequest(runtime, attrs.sub, API_SUBSCRIBER_ENGAGEMENT + '?authorSub=' + encodeURIComponent(attrs.sub) + '&newsletterId=' + encodeURIComponent(selectedItem.id))
      .then(data => {
        if (!Array.isArray(data.subscribers)) throw Error('Engagement unavailable');
        const subs = data.subscribers.map(s => {
          if (!s || typeof s !== 'object') throw Error('Engagement unavailable');
          return { ...s, email: typeof s.email === 'string' ? s.email : '', opened: s.opened === true, totalUniqueClicks: metric(s.totalUniqueClicks) || 0 };
        });
        const recipients = metric(data.totalRecipients) ?? subs.length;
        const whoOpened = subs.filter(s => s.opened), whoClicked = subs.filter(s => s.totalUniqueClicks > 0);
        if (active) setSelectedEngagement({ deliveryEventsAvailable:data.deliveryEventsAvailable !== false, unconfirmed:metric(data.unconfirmed), recipients, uniqueOpens: whoOpened.length, uniqueClicks: subs.reduce((n,s)=>n+s.totalUniqueClicks,0),
          delivered:subs.filter(s=>s.deliveredAt).length,bounced:subs.filter(s=>s.deliveryStatus==='bounced').length,complained:subs.filter(s=>s.deliveryStatus==='complained').length,
          openRate: recipients ? whoOpened.length/recipients*100 : null, clickRate: recipients ? whoClicked.length/recipients*100 : null,
          ctor: whoOpened.length ? whoOpened.filter(s => s.totalUniqueClicks > 0).length/whoOpened.length*100 : null, whoOpened, whoClicked });
      }).catch(() => { if (active) setEngagementError('Detailed engagement could not be loaded. Use Refresh mail to retry.'); });
    return () => { active = false; };
  }, [available, attrs, runtime, activeView, selectedItem, isSent, API_SUBSCRIBER_ENGAGEMENT]);

  if (checking) return <p role="status" className="p-6 text-bookish-purple">Loading Mail Center…</p>;
  if (!mailAddonEnabled) return <section className="p-6 space-y-4 bg-bookish-cream text-bookish-purple">
    <h1 className="text-2xl font-serif">Mail Center</h1>
    <p role={error ? 'alert' : 'status'}>{error || 'Complete your account setup in the portal to open Mail Center.'}</p>
    <button className="min-h-[44px] underline mr-4" onClick={refreshMail}>Retry Mail Center</button>
    <button className="min-h-[44px] underline" onClick={() => go(runtime.portalUrl)}>Return to Portal</button>
  </section>;
  /* ================= render ================= */

  return (
    <div className="min-h-screen w-full bg-bookish-cream text-bookish-purple overflow-hidden">
      {error && <p role="alert" className="p-4 text-red-700">{error}</p>}
      {/* TOP BAR */}
      <header className="w-full bg-bookish-royal text-bookish-cream">
        <div className="w-full px-3 md:px-2 py-3 flex flex-wrap gap-3 justify-between items-center">
          <h1 className="text-xl font-serif font-bold">
            Bookish Circle Mail Center
          </h1>

          <div className="flex flex-wrap items-center gap-4 font-semibold">
            <button className="min-h-[44px] underline focus-visible:outline focus-visible:outline-2" onClick={refreshMail}>Refresh mail</button>
            <NavLink onClick={event => { if (!leave()) event.preventDefault(); }} to="/market" className="hover:text-bookish-cream">
              Bookish Circle Market
            </NavLink>
            <button
              onClick={() => go(runtime.portalUrl)}
              className="min-h-[44px] px-2 py-1 rounded bg-white text-bookish-royal"
            >
              Return to Portal
            </button>
          </div>
        </div>
      </header>

      {/* CONTENT */}
      <div className="w-full mx-auto px-3 lg:px-6 py-2">
        <div className="mb-2">
          <h2 className="text-2xl font-serif text-bookish-royal">
            {attrs?.["custom:penname"] || "Mail Center"}
          </h2>
          <p className="text-sm text-bookish-royalAccent">
            {attrs?.["custom:authoremail"]}
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-3 min-h-[65vh] items-stretch">
          {/* Mobile sidebar toggle */}
          <button
            id="mail-menu-toggle" aria-expanded={sidebarOpen} aria-controls="mail-navigation" onClick={() => setSidebarOpen(value => !value)}
            className="md:hidden min-h-[44px] flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-bookish-purple/20 text-bookish-purple w-fit"
          >
            <Menu className="h-5 w-5" />
            <span className="text-sm font-medium">Menu</span>
          </button>

          <aside id="mail-navigation" aria-label="Mail Center navigation" className={`${sidebarOpen ? "block" : "hidden"} md:block w-full md:w-64 shrink-0 space-y-3`}>
            {/* Mobile close button */}
            <div className="md:hidden flex justify-end p-2">
              <button aria-label="Close Mail Center menu" onClick={() => { setSidebarOpen(false); document.getElementById('mail-menu-toggle')?.focus(); }} className="min-h-[44px] min-w-[44px] p-1 rounded-lg hover:bg-bookish-purple/10">
                <X className="h-5 w-5 text-bookish-purple" />
              </button>
            </div>
            <div className="bg-white p-3 rounded-xl border border-bookish-purple/40">
              {/* NEWSLETTERS */}
              <div className="mb-1">
                <h4 className="text-xs uppercase tracking-wide mb-1">
                  Newsletters
                </h4>
                {["compose newsletter", "newsletters", "newsletter drafts" ].map(
                  (id) => {
                    const m = Mailboxes.find((x) => x.id === id);
                    return (
                      <button
                        key={id}
                        onClick={() => id === "compose newsletter" ? go(runtime.composerUrl) : selectMailbox(id)}
                        aria-current={activeView === "mail" && selectedMailbox === id ? "page" : undefined}
                        className={`block min-h-[44px] w-full text-left px-3 py-2 rounded focus-visible:outline focus-visible:outline-2 ${activeView === "mail" && selectedMailbox === id ? "bg-bookish-royal/10 font-semibold" : "hover:bg-bookish-royal/10"}`}
                      >
                        {m.name}
                      </button>
                    );
                  }
                )}
              </div>

              <div className="my-1 h-px bg-bookish-purple/15" />

              {/* MAILBOX */}
              <div className="mb-1">
                <h4 className="text-xs uppercase tracking-wide mb-1">
                  Mailbox
                </h4>
                {["inbox", "sent", "deleted"].map((id) => {
                  const m = Mailboxes.find((x) => x.id === id);
                  return (
                    <button
                      key={id}
                      disabled
                      title="Personal mailboxes are not available yet"
                      className="block min-h-[44px] w-full text-left px-3 py-2 rounded opacity-60"
                    >
                      {m.name} — coming soon
                    </button>
                  );
                })}
              </div>

              <div className="my-1 h-px bg-bookish-purple/15" />

              {/* SUBSCRIBERS */}
              <div>
                <h4 className="text-xs uppercase tracking-wide mb-1">
                  Subscribers
                </h4>

                {/* Manage Subscribers */}
                <button
                  onClick={() => {
                    selectSubscribers("manage");
                  }}
                  className={`block min-h-[44px] w-full text-left px-3 py-2 rounded ${
                    activeView === "subscribers" && subscriberView === "manage"
                      ? "bg-bookish-royal/10 text-bookish-purple"
                      : "hover:bg-bookish-royal/10"
                  }`}
                >
                  Manage
                </button>

                {/* Import Subscribers */}
                <button
                  onClick={() => {
                    selectSubscribers("import");
                  }}
                  className={`block min-h-[44px] w-full text-left px-3 py-1 rounded ${
                    activeView === "subscribers" && subscriberView === "import"
                      ? "bg-bookish-royal/10 text-bookish-purple"
                      : "hover:bg-bookish-royal/10"
                  }`}
                >
                  Import
                </button>
              </div>
             </div> 

            {/* STATS */}
            <div className="bg-white p-4 rounded-xl border border-bookish-purple/40">
              <h3 className="font-serif font-bold mb-1">Newsletter Stats</h3><p className="text-xs mb-3">Based on recorded engagement. Email privacy features and link scanners can affect opens and clicks; acceptance does not guarantee inbox delivery.</p>
              {statsLoading ? (
                <div className="text-sm text-bookish-purpleAccent">Loading stats…</div>
              ) : statsError ? <p role="status" className="text-sm">{statsError}</p> : newsletterStats ? (
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Tracked recipients</span>
                    <span className="font-semibold">{newsletterStats.totalRecipients ?? newsletterStats.totalSubscribers ?? "—"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Open Rate</span>
                    <span className="font-semibold">
                      {newsletterStats.avgOpenRate != null
                        ? `${newsletterStats.avgOpenRate.toFixed(1)}%`
                        : "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Click Rate</span>
                    <span className="font-semibold">
                      {newsletterStats.avgClickRate != null
                        ? `${newsletterStats.avgClickRate.toFixed(1)}%`
                        : "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span title="Share of observed openers who also clicked">Click-to-open rate</span>
                    <span className="font-semibold">
                      {newsletterStats.avgCTOR != null
                        ? `${newsletterStats.avgCTOR.toFixed(1)}%`
                        : "—"}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-bookish-purpleAccent">No stats available</div>
              )}
            </div>
          </aside>

          {/* MAIN */}
          <main className="min-w-0 flex-1 flex flex-col lg:flex-row gap-3">
            {activeView === "subscribers" ? (
              <div className="flex-1 bg-white rounded-xl border border-bookish-purple/40 overflow-y-auto p-4">
                <Subscribers view={subscriberView} />
              </div>
            ) : (
              <>
                {/* THREADS */}
                <div className="w-full lg:w-64 shrink-0 bg-white p-3 rounded-xl border border-bookish-purple/40 max-h-[55vh] lg:max-h-[80vh] overflow-y-auto">
                  <h3 className="font-semibold mb-3">{mailboxTitle}</h3>

                  {listError && <p role="alert" className="mb-3 text-sm text-red-700">{listError} Use Refresh mail to retry.</p>}
                  {loadingList && items.length === 0 ? (
                    <div className="text-sm">Loading…</div>
                  ) : items.length === 0 ? (
                    <div className="text-sm">{listError ? 'The newsletter list is unavailable.' : isDraft ? 'No saved drafts yet. Choose Compose Newsletter to begin.' : 'No sent newsletters yet.'}</div>
                  ) : (
                    items.map((i) => (
                      <button
                        key={i.id}
                        aria-current={selectedItem?.id === i.id ? 'true' : undefined}
                        onClick={() => { setSelectedItem(i); setPreviewAttempt(0); if (window.matchMedia?.('(max-width: 1023px)').matches) { previewPanel.current?.focus({ preventScroll: true }); previewPanel.current?.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' }); } }}
                        className={`block min-h-[44px] w-full text-left p-2 rounded mb-1 ${
                          selectedItem?.id === i.id
                            ? "bg-bookish-royal/10"
                            : "hover:bg-bookish-royal/5"
                        }`}
                      >
                        <div className="font-medium">{i.subject}</div>
                        {i.deliveryStatus && <div className="text-xs">Send status: {i.deliveryStatus === 'sent' ? 'Submitted to email service' : i.deliveryStatus}</div>}
                        <div className="text-xs text-bookish-purpleAccent">
                          {formatShortDateTime(i.displayDate)}
                        </div>
                        {isSent && i.recipients != null && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            <span className="text-[10px] bg-bookish-royal/10 text-bookish-purple px-1.5 py-0.5 rounded">
                              {i.recipients} recipients
                            </span>
                            {i.openRate != null && (
                              <span className="text-[10px] bg-green-100 text-green-800 px-1.5 py-0.5 rounded">
                                {i.openRate.toFixed(1)}% opens
                              </span>
                            )}
                            {i.clickRate != null && (
                              <span className="text-[10px] bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">
                                {i.clickRate.toFixed(1)}% clicks
                              </span>
                            )}
                          </div>
                        )}
                      </button>
                    ))
                  )}
                  {nextToken && <button className="min-h-[44px] mt-3 underline" disabled={loadingList} onClick={() => { setPageToken(nextToken); setListAttempt(value => value + 1); }}>{loadingList ? 'Loading more…' : listError ? 'Retry loading more' : 'Load more newsletters'}</button>}
                </div>

                {/* PREVIEW */}
                <div ref={previewPanel} tabIndex={-1} aria-label="Selected newsletter" className="min-w-0 flex-1 bg-white p-3 rounded-xl border border-bookish-purple/40 overflow-x-auto">
                  {loadingPreview && <p role="status">Loading newsletter preview…</p>}
                  {previewError && <div role="alert"><p>{previewError}</p><button className="min-h-[44px] underline" onClick={() => setPreviewAttempt(value => value + 1)}>Retry preview</button></div>}
                  {!selectedItem && !loadingList && <p className="text-sm">Choose a newsletter to preview it here.</p>}
                  {preview && (
                    <>
                      <div className="lg:sticky lg:top-0 bg-white px-4 py-1 z-10">
                        <div className="flex flex-wrap justify-between gap-4">
                          <h3 className="text-2xl font-serif">
                            {preview.subject}
                          </h3>

                          {isDraft && selectedItem && (
                            <button
                              onClick={() =>
                                openDraftInComposer(selectedItem)
                              }
                              className="min-h-[44px] px-4 py-1.5 rounded-xl bg-bookish-purple text-white"
                            >
                              Continue Editing
                            </button>
                          )}
                        </div>
                      </div>

                      {engagementError && <p role="status" className="p-3 text-sm">{engagementError}</p>}
                      {isSent && preview.delivery?.accepted != null && <p className="mx-4 p-3 text-sm bg-bookish-cream rounded-xl">Email service accepted: {preview.delivery.accepted}. Failed: {preview.delivery.failed ?? '—'}. Uncertain: {preview.delivery.unknown ?? '—'}. Remaining: {preview.delivery.pending ?? '—'}. Skipped: {preview.delivery.skipped ?? '—'}. Stopped: {preview.delivery.cancelled ?? '—'}. Acceptance does not guarantee inbox delivery.</p>}
                      {isSent && selectedEngagement?.deliveryEventsAvailable && <p className="mx-4 text-sm">Recorded delivery events: {selectedEngagement.delivered} delivered, {selectedEngagement.bounced} bounced, {selectedEngagement.complained} complained. Older newsletters may not have delivery-event records.</p>}
                      {isSent && selectedEngagement?.deliveryEventsAvailable === false && <p className="mx-4 text-sm">Inbox delivery, bounce, and complaint reports are unavailable for group newsletters. Accepted messages are not confirmed inbox deliveries.</p>}
                      {isSent && selectedEngagement?.unconfirmed > 0 && <p className="mx-4 text-sm">{selectedEngagement.unconfirmed} prepared recipient records have no confirmed send or engagement evidence and are excluded from these rates. Check the delivery summary above.</p>}
                      {isSent && selectedEngagement && <p className="mx-4 text-sm">Opens and clicks are estimates: email privacy tools and link scanners can affect them. Click rate counts readers who clicked, not the number of links.</p>}
                      {/* PER-NEWSLETTER STATS PANEL */}
                      {isSent && (selectedEngagement || selectedItem) && (
                        <div className="mx-4 mb-2 p-3 bg-bookish-cream rounded-xl border border-bookish-purple/20">
                          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 text-center text-sm">
                            <div>
                              <div className="text-xs text-bookish-purpleAccent">Recipients</div>
                              <div className="font-semibold">{(selectedEngagement?.recipients ?? selectedItem?.recipients) ?? "—"}</div>
                            </div>
                            <div>
                              <div className="text-xs text-bookish-purpleAccent">Unique Opens</div>
                              <div className="font-semibold">{(selectedEngagement?.uniqueOpens ?? selectedItem?.uniqueOpens) ?? "—"}</div>
                            </div>
                            <div>
                              <div className="text-xs text-bookish-purpleAccent">Unique Clicks</div>
                              <div className="font-semibold">{(selectedEngagement?.uniqueClicks ?? selectedItem?.uniqueClicks) ?? "—"}</div>
                            </div>
                            <div>
                              <div className="text-xs text-bookish-purpleAccent">Open Rate</div>
                              <div className="font-semibold">
                                {(selectedEngagement?.openRate ?? selectedItem?.openRate) != null
                                  ? `${(selectedEngagement?.openRate ?? selectedItem?.openRate).toFixed(1)}%`
                                  : "—"}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-bookish-purpleAccent">Click Rate</div>
                              <div className="font-semibold">
                                {(selectedEngagement?.clickRate ?? selectedItem?.clickRate) != null
                                  ? `${(selectedEngagement?.clickRate ?? selectedItem?.clickRate).toFixed(1)}%`
                                  : "—"}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-bookish-purpleAccent">Click-to-open rate</div>
                              <div className="font-semibold">
                                {(selectedEngagement?.ctor ?? ((selectedItem?.openRate && selectedItem?.clickRate) ? (selectedItem.clickRate / selectedItem.openRate) * 100 : null)) != null
                                  ? `${(selectedEngagement?.ctor ?? ((selectedItem?.openRate && selectedItem?.clickRate) ? (selectedItem.clickRate / selectedItem.openRate) * 100 : null)).toFixed(1)}%`
                                  : "—"}
                              </div>
                            </div>
                          </div>

                          {/* Who Opened */}
                          {selectedEngagement?.whoOpened && selectedEngagement.whoOpened.length > 0 && (
                            <details className="mt-3">
                              <summary className="cursor-pointer text-sm font-semibold text-bookish-purple">
                                Who Opened ({selectedEngagement.whoOpened.length})
                              </summary>
                              <ul className="mt-1 max-h-40 overflow-y-auto text-xs space-y-1">
                                {selectedEngagement.whoOpened.map((sub) => (
                                  <li key={sub.email} className="flex justify-between px-2 py-1 bg-white rounded">
                                    <span>{sub.email}</span>
                                    <span className="text-bookish-purpleAccent">
                                      {sub.openedAt ? formatShortDateTime(sub.openedAt) : ""}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </details>
                          )}

                          {/* Who Clicked */}
                          {selectedEngagement?.whoClicked && selectedEngagement.whoClicked.length > 0 && (
                            <details className="mt-2">
                              <summary className="cursor-pointer text-sm font-semibold text-bookish-purple">
                                Who Clicked ({selectedEngagement.whoClicked.length})
                              </summary>
                              <ul className="mt-1 max-h-40 overflow-y-auto text-xs space-y-1">
                                {selectedEngagement.whoClicked.map((sub) => (
                                  <li key={sub.email} className="flex justify-between px-2 py-1 bg-white rounded">
                                    <span>{sub.email}</span>
                                    <span className="text-bookish-purpleAccent">
                                      {sub.totalUniqueClicks ?? 0} link{(sub.totalUniqueClicks ?? 0) !== 1 ? "s" : ""}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </details>
                          )}
                        </div>
                      )}

                      <iframe
                        title="Newsletter preview"
                        sandbox=""
                        className="w-full min-h-[600px] bg-white rounded-xl border"
                        srcDoc={preview.html}
                      />
                    </>
                  )}
                </div>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
